import { useRef, ChangeEvent, KeyboardEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import searchIcon from 'public/static/images/paid_landing/search-icon.svg';
import closeIcon from 'public/static/images/paid_landing/close.svg';
import { fonts, screen, colors } from 'components/styles/variables';
import TextSearchList from 'lib/TextSearchList';
import { useGeolocationData } from 'lib/hooks/useGeoLocation';
import { ESC, DOWN, UP, TAB, ENTER } from 'lib/keyCodes';
import Search, { performSearch } from 'lib/Search';
import useLockedScreen from 'lib/hooks/useLockedScreen';
import WindowLocation from 'lib/WindowLocation';
import { Result } from 'lib/Result';
import { ListItem } from 'types/TextSearch';
import useTextSearchReducer, {
  navedDown,
  navedUp,
  escaped,
  clickedOut,
  inputChanged,
  inputFocused,
} from 'redux-implementations/textSearch';
import { GeocodeAttributes } from 'types/Geocode';
import Dropdown from './Dropdown';

interface TextSearchInterface {
  fullList: TextSearchList;
  openModal: () => void;
  closeModal: () => void;
  isModal: boolean;
  onSearchInputTap?: () => Promise<void>;
  onSearchFieldSubmit: (item: ListItem) => void;
}

const defaultSearchLocation = {
  latitude: 30.2672,
  longitude: -97.7431,
  city: 'Austin',
  stateAbbreviation: 'TX',
  zip: '78701',
};

export const isExtraSmallDevice = (): boolean =>
  window.matchMedia(`(max-width: ${screen.xsMax})`).matches;

const TextSearch = ({
  fullList,
  openModal,
  closeModal,
  isModal,
  onSearchInputTap = (): Promise<void> => Promise.resolve(),
  onSearchFieldSubmit,
}: TextSearchInterface): JSX.Element => {
  const { t } = useTranslation();

  const push = WindowLocation.assign;
  const geolocation = useGeolocationData();
  const [state, dispatch] = useTextSearchReducer(fullList);
  const textInputRef: RefObject<HTMLInputElement> = useRef(null);
  const handleClickOutside = (): void => {
    dispatch(clickedOut);
  };
  useLockedScreen(state.dropDownOpen);

  // when a user is focused on the mobile input, and scrolls off,
  // it blurs and hides the keyboard
  const blurInput = (): void => {
    // react ref will not work
    const input = document.querySelector('input');
    if (input) {
      input.blur();
    }
    document.removeEventListener('touchmove', blurInput);
  };

  useClickAway(textInputRef, handleClickOutside);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(inputChanged(e.target.value));
  };

  function search(result: Result): void {
    let searchLocation: GeocodeAttributes | undefined = geolocation?.toJSON();
    if (searchLocation && searchLocation.latitude === 0 && searchLocation.longitude === 0) {
      searchLocation = defaultSearchLocation;
    }

    const searchObject = new Search({
      make: result.make,
      model: result.model,
      location: searchLocation,
      trim: result.trim,
      bodyType: result.bodyType,
    });

    performSearch(searchObject, push);
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    // arrow up/down button should select next/previous list element
    if (e.keyCode === UP) {
      e.preventDefault();
      dispatch(navedUp);
    } else if (e.keyCode === DOWN) {
      e.preventDefault();
      dispatch(navedDown);

      // enter key pressed
    } else if (e.keyCode === ENTER) {
      e.preventDefault();
      if (typeof state.cursor === 'number') {
        onSearchFieldSubmit(state.list[state.cursor]);
        const result = new Result(state.list[state.cursor]);
        search(result);
      }
      // escape key pressed
    } else if (e.keyCode === ESC || (state.dropDownOpen && e.keyCode === TAB)) {
      e.preventDefault();
      dispatch(escaped);
    }
  };
  const inputClass = isModal ? 'input-search-open' : '';
  const searchContainerClass = isModal ? 'search-open' : '';
  const autofocus = !!isModal;
  return (
    <div className="search-outer" ref={textInputRef}>
      <div className={`search-container ${searchContainerClass}`}>
        <div className="search-icon" />
        <input
          data-testid="text-search-input"
          role="search"
          type="text"
          onClick={onSearchInputTap}
          className={inputClass}
          value={state.textInput}
          onChange={handleChange}
          placeholder={t('paidLanding.textSearchCopy')}
          onFocus={(): void => {
            dispatch(inputFocused);

            if (isExtraSmallDevice() && !isModal) {
              openModal();
            }

            if (isModal) {
              document.addEventListener('touchmove', blurInput);
            }
          }}
          autoFocus={autofocus}
          spellCheck="false"
          onKeyDown={handleKeyDown}
        />
      </div>
      {isModal && <button className="x" onClick={closeModal} data-testid="close-text-search" />}

      <Dropdown
        list={state.list}
        query={state.textInput}
        hide={!state.dropDownOpen}
        cursor={state.cursor}
        triggerSearch={(item: ListItem): void => {
          onSearchFieldSubmit(item);
          const result = new Result(item);

          search(result);
        }}
      />
      <style jsx>{`
        .search-outer {
          width: 100%;
          display: flex;
          align-items: center;
          position: initial;
          border-radius: 8px;
          justify-content: center;
          max-width: 682px;
        }

        .search-container {
          width: 100%;
          position: relative;
          box-sizing: border-box;
          border-radius: 8px;
        }

        @media (min-width: ${screen.smMin}) {
          .search-outer {
            position: relative;
          }

          .search-container {
            max-width: 682px;
            border-radius: ${state.dropDownOpen ? '8px 8px 0px 0px' : '8px'};
            border: none;
          }
        }

        input {
          color: black;
          width: 100%;
          height: 48px;
          border-radius: 8px;
          outline: none;
          border: 1px solid ${colors.G2};
          font-weight: 400;
          font-size: 16px;
          padding: 5px 5px 5px 52px;
          font-family: ${fonts.default};
          box-sizing: border-box;
          -webkit-appearance: none;
        }

        @media (min-width: ${screen.smMin}) {
          input {
            border-radius: ${state.dropDownOpen ? '8px 8px 0px 0px' : '8px'};
            height: 56px;
            font-size: 18px;
            max-width: 682px;
            padding: 5px 0px 5px 67px;
          }
        }

        input[placeholder] {
          text-overflow: ellipsis;
        }

        input:focus[placeholder] {
          text-overflow: ellipsis;
        }

        .input-search-open {
          background-color: ${colors.G1};
          border: 1px solid ${colors.G2};
          // remove default mobile ios behavior of box shadow on input
          -webkit-appearance: none;
        }

        .input-search-open:focus {
          background-color: white;
          border: 1px solid ${colors.BLACK};
        }

        .search-open {
          width: 100%;
        }

        .search-icon {
          position: absolute;
          top: 11px;
          left: 12px;
          height: 26px;
          width: 26px;
          background: url(${searchIcon});
          background-size: cover;
        }

        @media (min-width: ${screen.smMin}) {
          .search-icon {
            top: 15px;
            left: 17px;
            height: 26.5px;
            width: 26px;
          }
        }

        .x {
          background: none;
          background-image: url(${closeIcon});
          background-size: cover;
          right: -30px;
          width: 30px;
          height: 30px;
          margin-left: 14px;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default TextSearch;
